import BigNumber from "bignumber.js";
import { formatUnits, parseUnits } from "viem";

export type BigNumberish = number | string | BigNumber | bigint;

export const convertBigNumberishToBigInt = (num: BigNumberish) => {
  if (typeof num === "number") return BigInt(Math.trunc(num));
  if (BigNumber.isBigNumber(num)) return BigInt(num.toFixed(0, 5));
  return BigInt(num);
};

export const limitDecimals = (amount: BigNumberish, maxDecimals?: number) => {
  let amountStr = amount.toString();
  if (maxDecimals === undefined) {
    return amountStr;
  }
  if (maxDecimals === 0) {
    return amountStr.split(".")[0];
  }
  const dotIndex = amountStr.indexOf(".");
  if (dotIndex !== -1) {
    let decimals = amountStr.length - dotIndex - 1;
    if (decimals > maxDecimals) {
      amountStr = amountStr.substring(
        0,
        amountStr.length - (decimals - maxDecimals)
      );
    }
  }
  return amountStr;
};

export const padDecimals = (amount: BigNumberish, minDecimals: number) => {
  let amountStr = amount.toString();
  const dotIndex = amountStr.indexOf(".");
  if (dotIndex !== -1) {
    const decimals = amountStr.length - dotIndex - 1;
    if (decimals < minDecimals) {
      amountStr = amountStr.padEnd(
        amountStr.length + (minDecimals - decimals),
        "0"
      );
    }
  } else {
    amountStr = amountStr + "." + "0".repeat(minDecimals);
  }
  return amountStr;
};

export const formatAmount = (
  amount: BigNumberish | undefined,
  tokenDecimals: number,
  displayDecimals?: number,
  defaultValue?: string | number,
  useCommas?: boolean,
  isNum: boolean = false
) => {
  if (!defaultValue) {
    defaultValue = "...";
  }
  if (amount === undefined || !amount?.toString()?.length) {
    return defaultValue;
  }
  if (displayDecimals === undefined) {
    displayDecimals = 4;
  }
  let amountStr = formatUnits(
    convertBigNumberishToBigInt(amount),
    tokenDecimals
  );

  amountStr = limitDecimals(amountStr, displayDecimals);
  if (displayDecimals !== 0) {
    amountStr = padDecimals(amountStr, displayDecimals);
  }
  if (isNum) {
    return Number(amountStr);
  }
  if (useCommas) {
    return numberWithCommas(amountStr);
  }
  return amountStr;
};

export function bigNumberify(n: BigNumberish) {
  try {
    if (typeof n === "bigint") return new BigNumber(n.toString());
    return new BigNumber(n);
  } catch (e) {
    console.error("bigNumberify error", e);
    return undefined;
  }
}

export const parseValue = (
  value: string,
  tokenDecimals: number,
  defaultValue = undefined
) => {
  const fValue = +value;
  if (
    !Number.isFinite(fValue) ||
    `${fValue}`.includes("e") ||
    `${fValue}`.includes("E")
  ) {
    return defaultValue;
  }
  const pValue = parseFloat(value);
  if (isNaN(pValue)) {
    return defaultValue;
  }
  value = limitDecimals(value, tokenDecimals);
  const amount = parseUnits(value, tokenDecimals);

  return bigNumberify(amount.toString());
};

export function numberWithCommas(
  x: BigNumberish | undefined,
  decimals?: number,
  padDecimals: boolean = false
) {
  if (x === undefined) {
    return "...";
  }

  var parts = x.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (typeof decimals === "number") {
    if (parts[1]) {
      parts[1] = parts[1].slice(0, decimals);
    } else if (padDecimals) {
      let zeroStr = "";
      new Array(decimals).fill("0").forEach((i) => (zeroStr = zeroStr + i));
      parts[1] = zeroStr;
    }
  }
  return parts.join(".");
}

export const formatWithUnit = (number: number | string, decimals = 1) => {
  let i = 0;
  let value = Number(number);

  if (!number) return "0";

  while (value > 1000) {
    value /= 1000;
    ++i;
  }

  return `${i ? value.toFixed(decimals) : value}${
    ["", "K", "M", "G", "T", "P"][i]
  }`;
};
